import React, { useState, useContext } from 'react';
import { AuthContext } from '@/App';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Shield, Sparkles, ArrowRight, X, Clock, ListChecks, Brain } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const steps = [
  {
    icon: Clock,
    color: '#00D9FF',
    headline: 'SpaceCal protects your time',
    body: 'Most apps just pack your day full. SpaceCal.ai is different — it schedules with breathing space built in, watches for overload, and protects what matters most to you.',
    cta: 'Got it',
  },
  {
    icon: ListChecks,
    color: '#a78bfa',
    headline: 'Add a task, AI finds the time',
    body: 'Head to Tasks and add what you need to do. Tell it the duration, energy needed, and priority. SpaceCal will find the best slot in your week — automatically.',
    cta: 'Makes sense',
  },
  {
    icon: Brain,
    color: '#f59e0b',
    headline: 'Your AI coach is always here',
    body: 'The ✨ button in the corner is Ask SpaceCal — your AI time coach. Ask it anything: "Am I overloaded?", "What should I focus on?", or "Help me plan this week."',
    cta: "Let's go →",
    final: true,
  },
];

const Tutorial = ({ onClose }) => {
  const { auth } = useContext(AuthContext);
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [dismissing, setDismissing] = useState(false);

  const complete = async (goToTasks = false) => {
    setDismissing(true);
    try {
      await axios.put(`${API}/tutorial/complete`, {}, { headers: { Authorization: `Bearer ${auth.token}` } });
    } catch {}
    onClose();
    if (goToTasks) navigate('/tasks');
  };

  const current = steps[step];
  const Icon = current.icon;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
      style={{ background: 'rgba(5,8,22,0.85)', backdropFilter: 'blur(8px)' }}>
      <div
        className={`relative w-full sm:max-w-md mx-4 mb-4 sm:mb-0 rounded-3xl overflow-hidden transition-all duration-300 ${dismissing ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}
        style={{ background: 'rgba(14,18,42,0.98)', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 40px 100px rgba(0,0,0,0.7)' }}>

        {/* Progress bar */}
        <div className="h-0.5" style={{ background: 'rgba(255,255,255,0.06)' }}>
          <div className="h-full transition-all duration-500 rounded-full"
            style={{ width: `${((step + 1) / steps.length) * 100}%`, background: current.color }}/>
        </div>

        {/* Dismiss */}
        <button onClick={() => complete(false)}
          className="absolute top-4 right-4 w-7 h-7 flex items-center justify-center rounded-full text-gray-600 hover:text-gray-300 hover:bg-white/8 transition-all">
          <X size={14}/>
        </button>

        <div className="px-8 py-8">
          {/* Icon */}
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6"
            style={{ background: `${current.color}15`, border: `1px solid ${current.color}30`, boxShadow: `0 4px 20px ${current.color}20` }}>
            <Icon size={24} style={{ color: current.color }}/>
          </div>

          {/* Step indicator */}
          <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: current.color }}>
            {step + 1} of {steps.length}
          </p>

          <h2 className="heading-font text-xl font-bold text-white mb-3 leading-tight">
            {current.headline}
          </h2>
          <p className="text-gray-400 text-sm leading-relaxed mb-8">
            {current.body}
          </p>

          {/* Actions */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => current.final ? complete(true) : setStep(s => s + 1)}
              className="flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl text-sm font-bold transition-all hover:opacity-90 active:scale-95"
              style={{ background: current.color, color: '#0A0E27', boxShadow: `0 4px 20px ${current.color}35` }}>
              {current.cta}
              {!current.final && <ArrowRight size={15}/>}
            </button>
            {step > 0 && (
              <button onClick={() => setStep(s => s - 1)}
                className="px-4 py-3 rounded-2xl text-sm text-gray-500 hover:text-gray-300 transition-colors"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                Back
              </button>
            )}
          </div>

          {/* Skip */}
          <button onClick={() => complete(false)}
            className="w-full text-center text-xs text-gray-600 hover:text-gray-400 mt-4 transition-colors">
            Skip intro
          </button>
        </div>

        {/* Step dots */}
        <div className="flex justify-center gap-1.5 pb-6">
          {steps.map((_, i) => (
            <div key={i} className="rounded-full transition-all"
              style={{ width: i === step ? 20 : 6, height: 6, background: i === step ? current.color : 'rgba(255,255,255,0.12)' }}/>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Tutorial;
